import { Env } from '@env';
import {
  CardElement,
  Elements,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import type { StripeCardElementChangeEvent } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { router, Stack } from 'expo-router';
import * as React from 'react';
import { Keyboard, KeyboardAvoidingView, Platform, View } from 'react-native';
import Modal from 'react-native-modal';
import Toast from 'react-native-toast-message';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { PaymentMethod } from '@/api/customers/types';
import { useCustomerPaymentMethodCreate } from '@/api/customers/use-payment-method-create';
import { useCustomerPaymentMethodDelete } from '@/api/customers/use-payment-method-delete';
import { useCustomerPaymentMethodSetDefault } from '@/api/customers/use-payment-method-set-default';
import { useCustomerPaymentMethods } from '@/api/customers/use-payment-methods';
import BackButton from '@/components/back-button';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import { type CardFormView } from '@/components/stripe';
import { isDesktop } from '@/core';
import { ERROR_LIGHT, GREY_30, GREY_40, GREY_70 } from '@/style/colors';
import { Button, showErrorMessage, Text } from '@/ui';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Icon } from '@/ui/icon';
import { ArrowRight } from '@/ui/icons';
import { Amex } from '@/ui/icons/amex';
import { Discover } from '@/ui/icons/discover';
import { Mastercard } from '@/ui/icons/mastercard';
import { Visa } from '@/ui/icons/visa';
import { ListItem } from '@/ui/list-item';
import { Loader } from '@/ui/loader';

const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

const StripeWrapper = () => {
  const { theme } = useStyles(stylesheet);

  if (!stripePromise) {
    return (
      <>
        <Stack.Screen
          options={{
            title: 'Payment Methods',
            headerTitle: 'Payment Methods',
            headerLeft: () => <BackButton />,
            headerShown: true,
          }}
        />

        <View
          style={{
            width: '100%',
            height: '100%',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: theme.colors.background_1,
          }}
        >
          <Loader style={{ height: 250, width: 250 }} />
        </View>
      </>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: 'en',
      }}
    >
      <Stack.Screen
        options={{
          title: 'Payment Methods',
          headerTitle: 'Payment Methods',
          headerLeft: () => <BackButton />,
          headerShown: true,
        }}
      />

      <PaymentMethods />
    </Elements>
  );
};

function PaymentMethods() {
  const { styles, theme } = useStyles(stylesheet);
  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    React.useState<PaymentMethod | null>();
  const [showAddCardModal, setShowAddCardModal] = React.useState(false);
  const [cardDetails, setCardDetails] = React.useState<CardFormView.Details>();
  const [deleteCardModalVisible, setDeleteCardModalVisible] =
    React.useState(false);
  const [modifyCardModalVisible, setModifyCardModalVisible] =
    React.useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();

  const { data: paymentMethods, isPending: isGettingPaymentMethods } =
    useCustomerPaymentMethods();

  const { mutate: deletePaymentMethod, isPending: isDeletingPaymentMethod } =
    useCustomerPaymentMethodDelete();

  const { mutate: addPaymentMethod, isPending: isCreatingPaymentMethod } =
    useCustomerPaymentMethodCreate();

  const {
    mutate: setDefaultPaymentMethod,
    isPending: isSettingDefaultPaymentMethod,
  } = useCustomerPaymentMethodSetDefault();

  const handleAddCreditCard = async () => {
    try {
      const cardElement = elements!.getElement('card');

      const { paymentMethod, error } = await stripe!.createPaymentMethod({
        type: 'card',
        card: cardElement!,
      });

      if (error) {
        showErrorMessage(error.message);
      } else {
        addPaymentMethod(
          {
            id: paymentMethod.id,
          },
          {
            onSuccess: (d: any) => {
              queryClient.setQueryData(
                ['customers/payment-methods'],
                [...paymentMethods!, d]
              );

              Keyboard.dismiss();
              setShowAddCardModal(false);

              Toast.show({
                type: 'success',
                text1: 'Payment method added',
              });

              setSelectedPaymentMethod(null);
            },
            onError: (e) => {
              showErrorMessage(e.message);
            },
          }
        );
      }
    } catch (e: any) {
      showErrorMessage(e.message);
    }
  };

  const handleSetDefaultCard = () => {
    setDefaultPaymentMethod(
      {
        id: selectedPaymentMethod!.id,
      },
      {
        onSuccess: (d: any) => {
          d.sort((a: any, b: any) => b.isDefault - a.isDefault);

          queryClient.setQueryData(['customers/payment-methods'], d);

          Toast.show({
            type: 'success',
            text1: 'Updated default payment method',
          });

          setModifyCardModalVisible(false);
          setSelectedPaymentMethod(null);
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  const deleteCard = () => {
    deletePaymentMethod(
      {
        id: selectedPaymentMethod!.id,
      },
      {
        onSuccess: () => {
          const newPaymentMethods = paymentMethods?.filter(
            (pm) => pm.id !== selectedPaymentMethod?.id
          );
          queryClient.setQueryData(
            ['customers/payment-methods'],
            newPaymentMethods
          );

          setSelectedPaymentMethod(null);
          Toast.show({
            type: 'success',
            text1: 'Payment method deleted',
          });
        },
        onError: (e) => {
          showErrorMessage(e.message);
        },
      }
    );
  };

  const handleRemoveCard = () => {
    setModifyCardModalVisible(false);
    setDeleteCardModalVisible(true);
  };

  const renderIcon = (brand: string) => {
    switch (brand) {
      case 'visa':
        return <Visa />;
      case 'mastercard':
        return <Mastercard />;
      case 'amex':
        return <Amex />;
      case 'discover':
        return <Discover />;
      default:
        return null;
    }
  };

  return (
    <>
      {Platform.OS === 'web' && isDesktop() ? (
        <Stack.Screen
          options={{
            headerShown: false,
          }}
        />
      ) : (
        <Stack.Screen
          options={{
            title: 'Payment Methods',
            headerTitle: 'Payment Methods',
            headerLeft: () => <BackButton />,
            headerShown: true,
          }}
        />
      )}

      <View
        style={{
          flexGrow: 1,
          backgroundColor: theme.colors.background_1,
          alignItems: 'center',
        }}
      >
        <View style={styles.container}>
          {isDesktop() ? (
            <View style={{ gap: 16 }}>
              <NavigationButton
                type={NavigationButtonTypes.back_with_text}
                onPress={() => {
                  router.navigate('/account');
                }}
              />

              <Text type="largeTitle">Payment Methods</Text>
            </View>
          ) : null}

          {(isGettingPaymentMethods ||
            isDeletingPaymentMethod ||
            isSettingDefaultPaymentMethod ||
            isCreatingPaymentMethod) && (
            <View style={{ width: '100%', alignItems: 'center' }}>
              <Loader style={{ width: 150, height: 150 }} />
            </View>
          )}

          {paymentMethods?.map((paymentMethod: PaymentMethod) => (
            <View
              key={paymentMethod.id}
              style={{
                flexDirection: 'row',
                width: '100%',
                justifyContent: 'space-between',
                borderBottomColor: theme.colors.card.default.borderColor,
                paddingBottom: 16,
                borderBottomWidth: 0.2,
              }}
            >
              <View
                style={{
                  flexDirection: 'row',
                  gap: 8,
                  alignItems: 'center',
                }}
              >
                <Icon
                  type="dressed"
                  icon={renderIcon(paymentMethod.brand)}
                  width={15}
                  height={15}
                />

                <View style={{ gap: 4 }}>
                  <Text type="bodyBold">{`${paymentMethod.brand.toUpperCase()} *****${
                    paymentMethod.last4
                  }`}</Text>

                  {paymentMethod.isDefault && (
                    <Button
                      type="secondaryExtraSmall"
                      tx="paymentMethods.defaultCard"
                      onPress={() => {
                        setSelectedPaymentMethod(paymentMethod);
                        setModifyCardModalVisible(true);
                      }}
                      style={{
                        width: 90,
                        height: 30,
                        paddingTop: 4,
                        paddingBottom: 4,
                        paddingLeft: 4,
                        paddingRight: 4,
                      }}
                    />
                  )}

                  <Text
                    type="caption1"
                    style={{ color: GREY_40 }}
                    tx="paymentMethods.expirationDate"
                    values={{
                      month: paymentMethod.expMonth,
                      year: paymentMethod.expYear,
                    }}
                  />
                </View>
              </View>

              <Button
                type="secondarySmall"
                tx="paymentMethods.editButton"
                rightIcon={
                  <Icon
                    type="naked"
                    icon={<ArrowRight />}
                    fillColor={theme.colors.button.secondary.default.text}
                  />
                }
                onPress={() => {
                  setSelectedPaymentMethod(paymentMethod);
                  setModifyCardModalVisible(true);
                }}
                style={{ width: 80 }}
              />
            </View>
          ))}

          <Button
            type="primary"
            tx="paymentMethods.addACreditCard"
            onPress={() => setShowAddCardModal(true)}
            style={{
              position: 'absolute',
              bottom: 24,
              alignSelf: 'center',
              width: '90%',
            }}
          />
        </View>
      </View>

      <ConfirmModal
        isVisible={deleteCardModalVisible}
        titleTx="paymentMethods.removeCard"
        bodyTx="paymentMethods.removeCardConfirm"
        leftButton={{
          tx: 'paymentMethods.cancel',
          onPress: () => {
            setDeleteCardModalVisible(false);
          },
        }}
        rightButton={{
          tx: 'paymentMethods.delete',
          onPress: () => {
            deleteCard();
            setDeleteCardModalVisible(false);
          },
        }}
        onDismiss={() => {
          setDeleteCardModalVisible(false);
        }}
      />

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={1000}
        isVisible={modifyCardModalVisible}
        hasBackdrop
        style={{
          margin: 0,
          alignItems: 'center',
          justifyContent: 'center',
          alignSelf: 'center',
        }}
        onBackdropPress={() => {
          setModifyCardModalVisible(false);
        }}
      >
        <View
          style={{
            height: 225,
            width: 400,
            marginTop: isDesktop() ? 0 : 'auto',
            backgroundColor: theme.colors.background_2,
            borderTopRightRadius: 16,
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: isDesktop() ? 16 : 0,
            borderBottomRightRadius: isDesktop() ? 16 : 0,
            padding: 24,
            flexDirection: 'column',
          }}
        >
          <View
            style={{
              width: '100%',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <View style={{ alignItems: 'center', flexGrow: 1 }}>
              <Text
                type="title2"
                tx="paymentMethods.edit"
                values={{
                  brand: selectedPaymentMethod?.brand.toUpperCase(),
                  last4: selectedPaymentMethod?.last4,
                }}
              />
            </View>

            <NavigationButton
              type={NavigationButtonTypes.cancel}
              onPress={() => {
                setModifyCardModalVisible(false);
                setSelectedPaymentMethod(null);
              }}
            />
          </View>

          <View
            style={{
              flexGrow: 1,
              justifyContent: 'flex-end',
              gap: 16,
              paddingBottom: 8,
            }}
          >
            <ListItem
              tx="paymentMethods.setDefaultCard"
              onPress={handleSetDefaultCard}
              disabled={selectedPaymentMethod?.isDefault}
            />

            <ListItem
              tx="paymentMethods.removeCard"
              onPress={handleRemoveCard}
              disabled={paymentMethods?.length === 1}
              textStyle={{ color: theme.colors.error }}
            />
          </View>
        </View>
      </Modal>

      <Modal
        animationIn="slideInUp"
        animationInTiming={300}
        animationOut="slideOutDown"
        animationOutTiming={1000}
        isVisible={showAddCardModal}
        hasBackdrop
        style={{
          margin: 0,
        }}
        onBackdropPress={() => {
          setShowAddCardModal(false);
          Keyboard.dismiss();
        }}
      >
        <KeyboardAvoidingView
          style={{
            flex: 1,
            pointerEvents: 'box-none',
            alignItems: 'center',
            justifyContent: 'center',
            alignSelf: 'center',
          }}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        >
          <View
            style={{
              width: isDesktop() ? 600 : 400,
              height: 250,
              marginTop: isDesktop() ? 0 : 'auto',
              backgroundColor: theme.colors.background_2,
              borderTopRightRadius: 16,
              borderTopLeftRadius: 16,
              borderBottomLeftRadius: isDesktop() ? 16 : 0,
              borderBottomRightRadius: isDesktop() ? 16 : 0,
              padding: 24,
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
          >
            <View
              style={{
                width: '100%',
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <View style={{ alignItems: 'center', flexGrow: 1 }}>
                <Text type="title2" tx="paymentMethods.addACard" />
              </View>

              <NavigationButton
                type={NavigationButtonTypes.cancel}
                onPress={() => {
                  setShowAddCardModal(false);
                  Keyboard.dismiss();
                }}
              />
            </View>

            <View
              style={{
                borderWidth: 1,
                borderColor: GREY_30,
                borderRadius: 4,
                backgroundColor: GREY_70,
              }}
            >
              <CardElement
                onChange={(cardDets: StripeCardElementChangeEvent) => {
                  console.log('cardDets', cardDets);
                  setCardDetails(cardDets);
                }}
                options={{
                  style: {
                    base: {
                      fontFamily: 'DMSans-Regular',
                      fontSmoothing: 'antialiased',
                      fontSize: '20px',
                      color: theme.colors.input.active.text,
                      '::placeholder': {
                        color: theme.colors.input.default.placeholderText,
                      },
                      backgroundColor:
                        theme.colors.card.default.backgroundColor,
                    },
                    invalid: {
                      color: ERROR_LIGHT,
                    },
                  },
                }}
              />
            </View>

            <View
              style={{
                gap: 16,
                paddingBottom: 8,
              }}
            >
              <Button
                type="primary"
                tx="paymentMethods.save"
                onPress={handleAddCreditCard}
                disabled={!cardDetails?.complete}
              />
            </View>
          </View>
        </KeyboardAvoidingView>
      </Modal>
    </>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 800,
    },
    width: {
      xs: '100%',
      md: 600,
    },
  },
}));

export default StripeWrapper;
